/**
 * Validators for condition dimensions whose value field is a list of
 * enumerated string values, plus an optional/required predicate.
 * For Media Devoted | Created By Ciro Autiero
 */

import {
  CONNECTION_TYPE_VALUES,
  DEVICE_TYPE_VALUES,
  PREDICATE_VALUES,
  WEEKDAY_VALUES,
} from '../enums';
import {
  describe,
  isPlainObject,
  joinPath,
  pushError,
  requireString,
  type ValidationCtx,
} from '../primitives';

function validateEnumArray(
  ctx: ValidationCtx,
  v: unknown,
  basePath: string,
  values: readonly string[],
): void {
  if (!Array.isArray(v)) {
    pushError(ctx, basePath, 'invalid_array', 'Expected array of enum values', {
      expected: 'array',
      received: describe(v),
    });
    return;
  }
  v.forEach((entry, idx) => {
    const path = joinPath(basePath, idx);
    if (typeof entry !== 'string') {
      pushError(ctx, path, 'wrong_type', 'Expected string enum value', {
        expected: values.join(' | '),
        received: describe(entry),
      });
      return;
    }
    if (!values.includes(entry)) {
      pushError(ctx, path, 'invalid_enum', `"${entry}" is not allowed`, {
        expected: values.join(' | '),
        received: entry,
      });
    }
  });
}

function requireEnumArray(
  ctx: ValidationCtx,
  raw: Record<string, unknown>,
  field: string,
  basePath: string,
  values: readonly string[],
): void {
  const path = joinPath(basePath, field);
  if (raw[field] === undefined) {
    pushError(
      ctx,
      path,
      'missing_required',
      `Missing required field "${field}"`,
    );
    return;
  }
  validateEnumArray(ctx, raw[field], path, values);
}

export function validateDeviceTypeCondition(
  ctx: ValidationCtx,
  raw: Record<string, unknown>,
  basePath: string,
): void {
  requireEnumArray(ctx, raw, 'deviceTypes', basePath, DEVICE_TYPE_VALUES);
  requireString(ctx, raw, 'predicate', basePath, PREDICATE_VALUES);
}

export function validateConnectionTypeCondition(
  ctx: ValidationCtx,
  raw: Record<string, unknown>,
  basePath: string,
): void {
  requireEnumArray(ctx, raw, 'types', basePath, CONNECTION_TYPE_VALUES);
  requireString(ctx, raw, 'predicate', basePath, PREDICATE_VALUES);
}

export function validateWeekdayCondition(
  ctx: ValidationCtx,
  raw: Record<string, unknown>,
  basePath: string,
): void {
  requireEnumArray(ctx, raw, 'weekdays', basePath, WEEKDAY_VALUES);
  const tzPath = joinPath(basePath, 'timeZone');
  if (raw.timeZone === undefined) {
    pushError(ctx, tzPath, 'missing_required', 'Missing required field "timeZone"');
  } else if (!isPlainObject(raw.timeZone)) {
    pushError(ctx, tzPath, 'invalid_object', 'timeZone must be an object');
  } else {
    requireString(ctx, raw.timeZone, 'code', tzPath);
  }
  requireString(ctx, raw, 'predicate', basePath, PREDICATE_VALUES);
}

export function validateProxyCondition(
  ctx: ValidationCtx,
  raw: Record<string, unknown>,
  basePath: string,
): void {
  const path = joinPath(basePath, 'proxyTypes');
  if (raw.proxyTypes === undefined) {
    pushError(ctx, path, 'missing_required', 'Missing required field "proxyTypes"');
  } else if (!Array.isArray(raw.proxyTypes)) {
    pushError(ctx, path, 'invalid_array', 'Expected array', {
      expected: 'string[]',
      received: describe(raw.proxyTypes),
    });
  } else {
    raw.proxyTypes.forEach((entry, idx) => {
      if (typeof entry !== 'string') {
        pushError(
          ctx,
          joinPath(path, idx),
          'wrong_type',
          'Expected string proxy type',
        );
      }
    });
  }
  requireString(ctx, raw, 'predicate', basePath, PREDICATE_VALUES);
}

export function validateUniqueVisitCondition(
  ctx: ValidationCtx,
  raw: Record<string, unknown>,
  basePath: string,
): void {
  requireString(ctx, raw, 'predicate', basePath, PREDICATE_VALUES);
}
